import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { getBeneProviders } from "../../../api";
import { StaleTime } from "../types";
import type { Provider } from "../../../@types";
import { useLoggedIn } from "../../loginHelpers";
import { useBeneinfoProvidersQueryKey } from "../queryKeyHooks";
import { useMemo } from "react";

/**
 * Fetch a logged-in bene's saved providers
 *
 * Synchronizes with `AppState.providers`, since that's the context for an
 * anonymous session's provider list
 *
 * RQ hook values are returned, if needed, but generally should just be called, `useBeneinfoProviders()`
 */
export const useBeneinfoProviders = (): Omit<
  Partial<UseQueryResult<Provider[], Error>>,
  "data"
> & {
  providers: Provider[];
} => {
  const beneinfoProvidersQueryKey = useBeneinfoProvidersQueryKey();
  const isLoggedIn = useLoggedIn();

  // * Queries
  const { data, error, isError, isLoading } = useQuery({
    enabled: isLoggedIn,
    queryKey: beneinfoProvidersQueryKey,
    queryFn: () => getBeneProviders(),
    staleTime: StaleTime.Infinite,
  });

  const providers = useMemo(() => data ?? [], [data]);

  // For use where needed. To just fetch saved providers, all the you need to do
  // is call the hook
  return {
    error,
    isError,
    isLoading,
    providers,
  };
};
